import { RefObject } from 'react';
import { RosterFilters } from '@/hooks/useRosterSearch';
import { CATEGORY_LABELS } from '@/types/nib/manualEntry';

interface RosterSearchBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  filters: RosterFilters;
  onFiltersChange: (update: Partial<RosterFilters>) => void;
  onClear: () => void;
  inputRef: RefObject<HTMLInputElement | null>;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  resultCount?: number;
  hasActiveFilters: boolean;
}

const SORT_OPTIONS = [
  { label: 'Name A-Z', sortBy: 'last_name' as const, sortOrder: 'asc' as const },
  { label: 'Name Z-A', sortBy: 'last_name' as const, sortOrder: 'desc' as const },
  { label: 'NI Number', sortBy: 'ni_number' as const, sortOrder: 'asc' as const },
  { label: 'Newest First', sortBy: 'inserted_date' as const, sortOrder: 'desc' as const },
  { label: 'Oldest First', sortBy: 'inserted_date' as const, sortOrder: 'asc' as const },
];

export function RosterSearchBar({
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  onClear,
  inputRef,
  isLoading,
  isError,
  errorMessage,
  resultCount,
  hasActiveFilters,
}: RosterSearchBarProps) {
  return (
    <div className="space-y-3">
      {/* Search input row */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search input */}
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {isLoading ? (
              <svg
                className="animate-spin h-5 w-5 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : (
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            )}
          </div>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search by name, NI number, or nickname..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-20 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            aria-label="Search employee profiles"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-1.5">
            {search && (
              <button
                onClick={() => onSearchChange('')}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
                aria-label="Clear search"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
            <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-xs text-gray-400 bg-gray-100 border border-gray-200 rounded">
              {navigator.platform.includes('Mac') ? '\u2318' : 'Ctrl'}K
            </kbd>
          </div>
        </div>

        {/* Category filter */}
        <select
          value={filters.category ?? ''}
          onChange={(e) =>
            onFiltersChange({
              category: e.target.value ? Number(e.target.value) : undefined,
            })
          }
          className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          aria-label="Filter by category"
        >
          <option value="">All Categories</option>
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        {/* Sort */}
        <select
          value={`${filters.sortBy}-${filters.sortOrder}`}
          onChange={(e) => {
            const opt = SORT_OPTIONS.find(
              (o) => `${o.sortBy}-${o.sortOrder}` === e.target.value
            );
            if (opt) onFiltersChange({ sortBy: opt.sortBy, sortOrder: opt.sortOrder });
          }}
          className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          aria-label="Sort employees"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={`${opt.sortBy}-${opt.sortOrder}`} value={`${opt.sortBy}-${opt.sortOrder}`}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Status row */}
      <div className="flex items-center justify-between text-sm">
        <div className="text-gray-500">
          {resultCount !== undefined && (
            <span>
              {resultCount} employee{resultCount !== 1 ? 's' : ''}
              {search && ' found'}
            </span>
          )}
        </div>
        {hasActiveFilters && (
          <button
            onClick={onClear}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Clear all filters
          </button>
        )}
      </div>

      {/* Error state */}
      {isError && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">
            {errorMessage || 'Failed to load profiles. Please try again.'}
          </p>
        </div>
      )}
    </div>
  );
}
